import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CalendlyAvailabilityPanel = () => {
  const [isConfigured, setIsConfigured] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncStatus, setSyncStatus] = useState('idle'); // idle, syncing, success, error

  useEffect(() => {
    checkCalendlyConfiguration();
  }, []);

  const checkCalendlyConfiguration = async () => {
    try {
      setLoading(true);
      setError(null);
      const { calendlyService } = await import('../../../services/calendlyService');
      
      const configured = calendlyService?.isConfigured();
      setIsConfigured(configured);

      if (configured) {
        const types = await calendlyService?.getEventTypes();
        setEventTypes(types);
      }
    } catch (err) {
      console.error('Error checking Calendly configuration:', err);
      // Check if it's an authentication error
      if (err?.message?.includes('Invalid Calendly API token')) {
        setError('Invalid API token. Please update your Calendly credentials in the .env file.');
      } else {
        setError('Failed to connect to Calendly');
      }
      setIsConfigured(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncAvailability = async () => {
    try {
      setSyncStatus('syncing');
      const { calendlyService } = await import('../../../services/calendlyService');
      
      // Get next 30 days of availability
      const startDate = new Date()?.toISOString();
      const endDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString();

      // Fetch scheduled events to understand booked slots
      const scheduledEvents = await calendlyService?.getScheduledEvents(startDate, endDate);
      
      setSyncStatus('success');
      setTimeout(() => setSyncStatus('idle'), 3000);
    } catch (err) {
      console.error('Error syncing Calendly availability:', err);
      setSyncStatus('error');
      if (err?.message?.includes('Invalid Calendly API token')) {
        setError('Invalid API token. Please update your Calendly credentials.');
      }
      setTimeout(() => setSyncStatus('idle'), 3000);
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center animate-pulse">
            <Icon name="Calendar" size={24} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Calendly Integration</h3>
            <p className="text-sm text-muted-foreground font-caption">Loading configuration...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isConfigured) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-lg bg-warning/10 flex items-center justify-center">
            <Icon name="AlertCircle" size={24} className="text-warning" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Calendly Not Configured</h3>
            <p className="text-sm text-muted-foreground font-caption">
              {error || 'Set up your Calendly API credentials'}
            </p>
          </div>
        </div>
        <div className="bg-muted/50 rounded-lg p-4 space-y-2">
          <p className="text-sm text-foreground font-medium">Required Environment Variables:</p>
          <ul className="text-xs text-muted-foreground font-caption space-y-1 ml-4">
            <li>• VITE_CALENDLY_API_TOKEN (must be a valid token, not placeholder)</li>
            <li>• VITE_CALENDLY_USER_URI</li>
          </ul>
          <p className="text-xs text-muted-foreground font-caption mt-3">
            Get your API token from{' '}
            <a
              href="https://calendly.com/integrations/api_webhooks"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Calendly Integrations
            </a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center">
            <Icon name="CheckCircle" size={24} className="text-success" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Calendly Connected</h3>
            <p className="text-sm text-muted-foreground font-caption">
              {eventTypes?.length} event type{eventTypes?.length !== 1 ? 's' : ''} available
            </p>
          </div>
        </div>
        <Button
          variant={syncStatus === 'success' ? 'default' : 'outline'}
          size="sm"
          onClick={handleSyncAvailability}
          disabled={syncStatus === 'syncing'}
          iconName={syncStatus === 'syncing' ? 'Loader' : syncStatus === 'success' ? 'CheckCircle' : 'RefreshCw'}
          iconPosition="left"
        >
          {syncStatus === 'syncing' ? 'Syncing...' : syncStatus === 'success' ? 'Synced' : 'Sync Availability'}
        </Button>
      </div>

      {eventTypes?.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm font-medium text-foreground">Active Event Types:</p>
          <div className="space-y-2">
            {eventTypes?.map((eventType) => (
              <div
                key={eventType?.id}
                className="bg-muted/30 rounded-lg p-3 flex items-center justify-between"
              >
                <div>
                  <p className="text-sm font-medium text-foreground">{eventType?.name}</p>
                  <p className="text-xs text-muted-foreground font-caption">
                    {eventType?.duration} minutes
                  </p>
                </div>
                <a
                  href={eventType?.schedulingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary/80 transition-colors"
                >
                  <Icon name="ExternalLink" size={16} />
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {syncStatus === 'error' && (
        <div className="mt-4 bg-destructive/10 border border-destructive/20 rounded-lg p-3 flex items-center gap-2">
          <Icon name="AlertCircle" size={16} className="text-destructive" />
          <p className="text-sm text-destructive">
            {error || 'Failed to sync availability. Please try again.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default CalendlyAvailabilityPanel;
